import { Tabs, Table, Space, Button, Tag, Input, message, Pagination, DatePicker, Dropdown, Modal, Segmented } from "antd";
import { useEffect, useState, useRef } from "react";
import { SearchOutlined, CalendarOutlined, DownloadOutlined } from "@ant-design/icons";
import type { InputRef, TableColumnType } from "antd";
import type { FilterDropdownProps } from "antd/es/table/interface";
import dayjs from "dayjs";
import PaymentModal from "./PaymentModal";
import BulkAuthorizeSheetModal from "./BulkAuthorizeSheetModal";
import ZohoReconcile from "./ZohoReconcileModal";
import CreditByClient from "./CreditByClient";
import {
  fetchProcurementPayments,
  authorizeProcurementPayment,
  bulkAuthorizeProcurementPayments,
  fetchProcurementCredits,
  rejectProcurementPayment,
} from "../../apis/procurement.api";
import { getFormattedDateAndTime } from "../../helpers/date_helper";
import { usePermissions } from "../../custom_hooks/permission/permission_hook";

const { TabPane } = Tabs;
const { RangePicker } = DatePicker;

const PaymentTabs = () => {
  const { hasPermission } = usePermissions();
  const canManage = hasPermission("finance.create");
  const [activeTab, setActiveTab] = useState('payments');
  const [searchText, setSearchText] = useState('');
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [currentPayment, setCurrentPayment] = useState<any>(null);
  const [isCreditConversion, setIsCreditConversion] = useState(false);
  const [isAuthorizing, setIsAuthorizing] = useState(false);
  const [selectedRowKeys, setSelectedRowKeys] = useState<number[]>([]);
  const [isSheetModalVisible, setIsSheetModalVisible] = useState(false);

  const [payments, setPayments] = useState<any[]>([]);
  const [credits, setCredits] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [filters, setFilters] = useState<Record<string, any>>({});
  const [ordering, setOrdering] = useState<string>('');
  const [savingPayment, setSavingPayment] = useState(false);
  const [isRejectModalVisible, setIsRejectModalVisible] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  const [rejecting, setRejecting] = useState(false);
  const [creditView, setCreditView] = useState<'order' | 'client'>('order');
  const [refreshToken, setRefreshToken] = useState(0);
  const searchInput = useRef<InputRef>(null);

  const isClientCreditView = activeTab === 'credit' && creditView === 'client';

  const getColumnSearchProps = (dataIndex: string): TableColumnType<any> => ({
    filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }: FilterDropdownProps) => (
      <div style={{ padding: 8 }} onKeyDown={(e) => e.stopPropagation()}>
        <Input
          ref={searchInput}
          placeholder={`Search ${dataIndex}`}
          value={selectedKeys[0]}
          onChange={(e) => setSelectedKeys(e.target.value ? [e.target.value] : [])}
          onPressEnter={() => confirm()}
          style={{ marginBottom: 8, display: 'block' }}
        />
        <Space>
          <Button
            type="primary"
            onClick={() => confirm()}
            icon={<SearchOutlined />}
            size="small"
            style={{ width: 90 }}
          >
            Search
          </Button>
          <Button
            onClick={() => { clearFilters?.(); confirm(); }}
            size="small"
            style={{ width: 90 }}
          >
            Reset
          </Button>
        </Space>
      </div>
    ),
    filterIcon: (filtered: boolean) => (
      <SearchOutlined style={{ color: filtered ? '#1677ff' : undefined }} />
    ),
    onFilterDropdownOpenChange: (visible) => {
      if (visible) {
        setTimeout(() => searchInput.current?.select(), 100);
      }
    },
  });

  const getDateRangeFilterProps = (dataIndex: string): TableColumnType<any> => ({
    filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }: FilterDropdownProps) => {
      const parsed = selectedKeys[0] ? JSON.parse(selectedKeys[0] as string) : null;
      return (
        <div style={{ padding: 8 }} onKeyDown={(e) => e.stopPropagation()}>
          <RangePicker
            value={parsed ? [dayjs(parsed[0]), dayjs(parsed[1])] : null}
            onChange={(dates) => {
              if (dates && dates[0] && dates[1]) {
                setSelectedKeys([
                  JSON.stringify([
                    dates[0].startOf('day').toISOString(),
                    dates[1].endOf('day').toISOString(),
                  ]),
                ]);
              } else {
                setSelectedKeys([]);
              }
            }}
            style={{ marginBottom: 8 }}
          />
          <div>
            <Space>
              <Button
                type="primary"
                onClick={() => confirm()}
                size="small"
                style={{ width: 90 }}
              >
                Filter
              </Button>
              <Button
                onClick={() => { clearFilters?.(); confirm(); }}
                size="small"
                style={{ width: 90 }}
              >
                Reset
              </Button>
            </Space>
          </div>
        </div>
      );
    },
    filterIcon: (filtered: boolean) => (
      <CalendarOutlined style={{ color: filtered ? '#1677ff' : undefined }} />
    ),
  });

  const buildFilterParams = (f: Record<string, any>) => {
    const params: Record<string, any> = {};
    Object.entries(f).forEach(([key, value]) => {
      if (!value || (Array.isArray(value) && value.length === 0)) return;

      if (key === 'created_at' && value[0]) {
        const [start, end] = JSON.parse(value[0]);
        params['created_at_after'] = start;
        params['created_at_before'] = end;
      } else if (Array.isArray(value)) {
        params[key] = value.join(',');
      }
    });
    return params;
  };

  const loadData = async (tab = activeTab, p = page, s = searchText, f = filters, o = ordering) => {
    // The client roll-up owns its own fetching; bumping the token is what refreshes it
    // after a payment or rejection made from either view.
    setRefreshToken((t) => t + 1);
    if (tab === 'credit' && creditView === 'client') return;

    setLoading(true);
    const filterParams = buildFilterParams(f);
    const params: Record<string, any> = { page: p, search: s, ...filterParams };
    if (o) params.ordering = o;
    let res;
    if (tab === "payments") {
      res = await fetchProcurementPayments(params);
    } else {
      res = await fetchProcurementCredits(params);
    }

    if (res) {
      const isArray = Array.isArray(res);
      const data = isArray ? res : (res.results || []);
      const count = isArray ? res.length : (res.count || 0);

      if (tab === "payments") {
        setPayments(data);
      } else {
        setCredits(data);
      }
      setTotalItems(count);
    }
    setLoading(false);
  };

  useEffect(() => {
    loadData(activeTab, page, searchText, filters, ordering);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, page, searchText, filters, ordering, creditView]);

  const formatDate = (value: string) => {
    if (!value) return '-';
    const date = new Date(value);
    return `${date.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })} ${date.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })}`;
  };

  const generateCSV = (data: any[], type: 'payments' | 'credit', title: string) => {
    const escapeCSVField = (field: string) => {
      if (field == null) return '';
      const stringField = String(field);
      // If field contains comma, newline, or quote, wrap in quotes and escape quotes
      if (stringField.includes(',') || stringField.includes('\n') || stringField.includes('\r') || stringField.includes('"')) {
        return `"${stringField.replace(/"/g, '""')}"`;
      }
      return stringField;
    };

    let csvContent = '';
    let headers: string[];

    if (type === 'payments') {
      headers = ['Lead ID', 'Branch', 'Amount', 'Created By', 'Mode', 'Payment Medium', 'Payment ID', 'Status', 'Created At', 'Estimate Status'];
      csvContent += headers.map(escapeCSVField).join(',') + '\n';

      data.forEach((row) => {
        const rowData = [
          row.lead_id || '-',
          row.branch_name || '-',
          `Rs. ${(Number(row.payment_amount) / 100).toLocaleString()}`,
          row.created_by_name || '-',
          row.payment_method?.toUpperCase() || '-',
          row.payment_medium || '-',
          row.payment_id || '-',
          row.status?.toUpperCase() || '-',
          formatDate(row.created_at),
          row.estimate_status ? row.estimate_status.replace(/_/g, ' ').toUpperCase() : '-',
        ];
        csvContent += rowData.map(escapeCSVField).join(',') + '\n';
      });
    } else {
      headers = ['Lead ID', 'Branch', 'Created By', 'Amount', 'Mode', 'Payment ID', 'Created At', 'Estimate Status'];
      csvContent += headers.map(escapeCSVField).join(',') + '\n';

      data.forEach((row) => {
        const rowData = [
          row.lead_id || '-',
          row.branch_name || '-',
          row.created_by_name || '-',
          `Rs. ${(Number(row.payment_amount) / 100).toLocaleString()}`,
          row.payment_method?.toUpperCase() || '-',
          row.payment_id || '-',
          formatDate(row.created_at),
          row.estimate_status ? row.estimate_status.replace(/_/g, ' ').toUpperCase() : '-',
        ];
        csvContent += rowData.map(escapeCSVField).join(',') + '\n';
      });
    }

    // Create and download CSV file
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `${type}_${dayjs().format('YYYY-MM-DD_HH-mm')}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleDownloadCurrentView = () => {
    const data = activeTab === 'payments' ? payments : credits;
    if (!data.length) {
      message.warning('No data to download');
      return;
    }
    generateCSV(data, activeTab as 'payments' | 'credit', `${activeTab === 'payments' ? 'Payments' : 'Credits'} - Current View`);
  };

  const handleDownloadAll = async () => {
    const hide = message.loading('Fetching all records...', 0);
    try {
      const filterParams = buildFilterParams(filters);
      const params: Record<string, any> = { page_size: 10000, search: searchText, ...filterParams };
      if (ordering) params.ordering = ordering;

      let res;
      if (activeTab === 'payments') {
        res = await fetchProcurementPayments(params);
      } else {
        res = await fetchProcurementCredits(params);
      }

      const data = Array.isArray(res) ? res : (res?.results || []);
      if (!data.length) {
        message.warning('No data to download');
        return;
      }
      generateCSV(data, activeTab as 'payments' | 'credit', `${activeTab === 'payments' ? 'Payments' : 'Credits'} - All Records`);
    } catch {
      message.error('Failed to fetch data for download');
    } finally {
      hide();
    }
  };


  const handleEdit = (record: any) => {
    setCurrentPayment(record);
    setIsCreditConversion(false);
    setIsAuthorizing(false);
    setIsModalVisible(true);
  };

  const handleAuthorizeClick = (record: any) => {
    setCurrentPayment(record);
    setIsCreditConversion(false);
    setIsAuthorizing(true);
    setIsModalVisible(true);
  };

  const handleConvertToPayment = (record: any) => {
    setCurrentPayment(record);
    setIsCreditConversion(true);
    setIsAuthorizing(false);
    setIsModalVisible(true);
  };

  const handleRejectClick = (record: any) => {
    setCurrentPayment(record);
    setRejectReason('');
    setIsRejectModalVisible(true);
  };

  const handleConfirmReject = async () => {
    if (!canManage) {
      message.error("You don't have permission to reject payments");
      return;
    }
    if (!currentPayment) return;

    const reason = rejectReason.trim();
    if (!reason) {
      message.warning('Please enter a reason for rejection');
      return;
    }

    setRejecting(true);
    try {
      const res = await rejectProcurementPayment(currentPayment.id, reason);
      if (res) {
        message.success('Payment rejected successfully');
        setIsRejectModalVisible(false);
        setCurrentPayment(null);
        setRejectReason('');
        loadData();
      } else {
        message.error('Failed to reject payment');
      }
    } catch (error) {
      message.error('Error rejecting payment');
      console.error('Reject payment error:', error);
    } finally {
      setRejecting(false);
    }
  };

  const handleSavePayment = async (values: any) => {
    if (!canManage) {
      message.error("You don't have permission to authorize payments");
      return;
    }
    if (!currentPayment) return;

    setSavingPayment(true);
    try {
      const isConvertToCredit = isAuthorizing && !isCreditConversion && values.mode === 'credit';
      const res = await authorizeProcurementPayment(currentPayment.id, {
        payment_id: values.payment_id,
        payment_method: values.mode,
        payment_medium: values.mode === 'credit' ? undefined : values.medium,
        payment_amount: Math.round(Number(values.payment_amount) * 100),
        created_at: values.created_at ? values.created_at.format('YYYY-MM-DD') : undefined,
      });

      if (res) {
        const successMessage = isConvertToCredit
          ? 'Payment converted to credit successfully'
          : isAuthorizing
            ? 'Payment authorized successfully'
            : isCreditConversion
              ? 'Credit converted successfully'
              : 'Payment updated successfully';
        // The payment is authorized either way — the Zoho document is a separate leg
        // that a nightly backfill retries. Said plainly so nobody re-authorizes a
        // payment that was already taken, trying to make Zoho catch up.
        if (res.zoho_synced === false) {
          message.warning(
            `${successMessage}, but it has not reached Zoho yet — it will be pushed automatically tonight. No action needed.`,
            8,
          );
        } else {
          message.success(successMessage);
        }
        setIsModalVisible(false);
        setCurrentPayment(null);
        loadData();
      } else {
        message.error('Failed to save payment changes');
      }
    } catch (error) {
      message.error('Error saving payment changes');
      console.error('Save payment error:', error);
    } finally {
      setSavingPayment(false);
    }
  };

  const handleBulkAuthorize = async () => {
    if (!canManage) {
      message.error("You don't have permission to authorize payments");
      return;
    }
    if (selectedRowKeys.length === 0) {
      message.warning('Please select at least one payment to authorize');
      return;
    }

    setLoading(true);
    try {
      const res = await bulkAuthorizeProcurementPayments(selectedRowKeys, {});
      
      if (res) {
        if (res.failed_payments && res.failed_payments.length > 0) {
          message.warning(`Authorized ${res.success_count} payments. ${res.failed_payments.length} failed.`);
          console.log('Failed payments:', res.failed_payments);
        } else {
          message.success(`Successfully authorized ${res.success_count} payments`);
        }
        
        setSelectedRowKeys([]); // Clear selection
        loadData(); // Refresh data
      } else {
        message.error('Failed to authorize payments');
      }
    } catch (error) {
      message.error('Error authorizing payments');
      console.error('Bulk authorize error:', error);
    } finally {
      setLoading(false);
    }
  };

  const paymentColumns = [
    {
      title: 'Lead ID',
      dataIndex: 'lead_id',
      key: 'lead_id',
      width: 180,
      fixed: 'left' as const,
      ...getColumnSearchProps('lead_id'),
    },
    {
      title: 'Amount',
      dataIndex: 'payment_amount',
      key: 'payment_amount',
      width: 110,
      render: (amount: any) => `\u20B9${(Number(amount) / 100).toLocaleString()}`,
      sorter: true,
      ...getColumnSearchProps('payment_amount'),
    },
    {
      title: 'Created By',
      dataIndex: 'created_by_name',
      key: 'created_by_name',
      width: 140,
      ...getColumnSearchProps('created_by_name'),
    },
    {
      title: 'Mode',
      dataIndex: 'payment_method',
      key: 'payment_method',
      width: 130,
      render: (method: string) => <Tag color={method === 'waive-off' ? 'purple' : 'blue'}>{method?.toUpperCase()}</Tag>,
      filters: [
        { text: 'Cash', value: 'cash' },
        { text: 'Card', value: 'card' },
        { text: 'NEFT_RTGS_IMPS', value: 'neft_rtgs_imps' },
        { text: 'UPI', value: 'upi' },
        { text: 'Cheque', value: 'cheque' },
        { text: 'NET_BANKING', value: 'net_banking' },
        { text: 'POS_MANUAL', value: 'pos-manual' },
        { text: 'Waive Off', value: 'waive-off' },
      ],
    },
    {
      title: 'Medium',
      dataIndex: 'payment_medium',
      key: 'payment_medium',
      width: 110,
      ...getColumnSearchProps('payment_medium'),
    },
    {
      title: 'Payment ID',
      dataIndex: 'payment_id',
      key: 'payment_id',
      width: 160,
      ...getColumnSearchProps('payment_id'),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 130,
      render: (status: string) => {
        const colorMap: any = {
          authorized: 'blue',
          authenticated: 'orange',
          captured: 'green',
        };
        return <Tag color={colorMap[status] || 'default'}>{status?.toUpperCase()}</Tag>;
      },
      filters: [
        { text: 'Authorized', value: 'authorized' },
        { text: 'Authenticated', value: 'authenticated' },
        { text: 'Captured', value: 'captured' },
      ],
    },
    {
      title: 'Created At',
      dataIndex: 'created_at',
      key: 'created_at',
      width: 160,
      sorter: true,
      render: (value: string) => {
        if (!value) return '-';
        const date = new Date(value);
        return (
          <span>
            {date.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}{' '}
            {date.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })}
          </span>
        );
      },
      ...getDateRangeFilterProps('created_at'),
    },
    {
      title: 'Estimate Status',
      dataIndex: 'estimate_status',
      key: 'estimate_status',
      width: 160,
      render: (s: string) => s ? <Tag color="geekblue">{s.replace(/_/g, ' ').toUpperCase()}</Tag> : '-',
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 180,
      fixed: 'right' as const,
      render: (_: any, record: any) => (
        <Space size="middle">
          {record.status === 'authenticated' ? (
            <>
              <Button type="primary" size="small" onClick={() => handleAuthorizeClick(record)}>
                Authorize
              </Button>
              <Button danger size="small" onClick={() => handleRejectClick(record)}>
                Reject
              </Button>
            </>
          ) : (
            <Button type="link" onClick={() => handleEdit(record)}>
              Edit
            </Button>
          )}
        </Space>
      ),
    },
  ];

  const creditColumns = [
    {
      title: 'Lead ID',
      dataIndex: 'lead_id',
      key: 'lead_id',
      width: 180,
      fixed: 'left' as const,
      ...getColumnSearchProps('lead_id'),
    },
    {
      title: 'Created By',
      dataIndex: 'created_by_name',
      key: 'created_by_name',
      width: 140,
      ...getColumnSearchProps('created_by_name'),
    },
    {
      title: 'Amount',
      dataIndex: 'payment_amount',
      key: 'payment_amount',
      width: 110,
      render: (amount: any) => `\u20B9${(Number(amount) / 100).toLocaleString()}`,
      sorter: true,
      ...getColumnSearchProps('payment_amount'),
    },
    {
      title: 'Mode',
      dataIndex: 'payment_method',
      key: 'payment_method',
      width: 130,
      render: (method: string) => <Tag color={method === 'cash' ? 'orange' : 'gold'}>{method?.toUpperCase()}</Tag>,
      filters: [
        { text: 'Cash', value: 'cash' },
        { text: 'Card', value: 'card' },
        { text: 'NEFT_RTGS_IMPS', value: 'neft_rtgs_imps' },
        { text: 'UPI', value: 'upi' },
        { text: 'Cheque', value: 'cheque' },
        { text: 'NET_BANKING', value: 'net_banking' },
        { text: 'POS_MANUAL', value: 'pos-manual' },
      ],
    },
    {
      title: 'Payment ID',
      dataIndex: 'payment_id',
      key: 'payment_id',
      width: 160,
      ...getColumnSearchProps('payment_id'),
    },
    {
      title: 'Created At',
      dataIndex: 'created_at',
      key: 'created_at',
      width: 160,
      sorter: true,
      render: (value: string) => {
        if (!value) return '-';
        const date = new Date(value);
        return (
          <span>
            {date.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}{' '}
            {date.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })}
          </span>
        );
      },
      ...getDateRangeFilterProps('created_at'),
    },
    {
      title: 'Estimate Status',
      dataIndex: 'estimate_status',
      key: 'estimate_status',
      width: 160,
      render: (s: string) => s ? <Tag color="geekblue">{s.replace(/_/g, ' ').toUpperCase()}</Tag> : '-',
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 240,
      fixed: 'right' as const,
      render: (_: any, record: any) => (
        <Space size="middle">
          <Button
            type="primary"
            size="small"
            onClick={() => handleConvertToPayment(record)}
          >
            Convert to Payment
          </Button>
          <Button danger size="small" onClick={() => handleRejectClick(record)}>
            Reject
          </Button>
        </Space>
      ),
    },
  ];

  return (
    <div className="payment-tabs">
      <PaymentModal
        visible={isModalVisible}
        onCancel={() => {
          setIsModalVisible(false);
          setCurrentPayment(null);
        }}
        onSave={handleSavePayment}
        initialValues={currentPayment ? {
          leadId: currentPayment.lead_id,
          mode: currentPayment.payment_method,
          payment_id: currentPayment.payment_id,
          medium: currentPayment.payment_medium,
          status: currentPayment.status,
          payment_amount: Number(currentPayment.payment_amount) / 100,
          created_at: currentPayment.created_at ? dayjs(currentPayment.created_at) : undefined
        } : {}}
        isCreditConversion={isCreditConversion}
        isAuthorizing={isAuthorizing}
        loading={savingPayment}
      />
      <BulkAuthorizeSheetModal
        visible={isSheetModalVisible}
        onCancel={() => setIsSheetModalVisible(false)}
        onCompleted={() => {
          setIsSheetModalVisible(false);
          setSelectedRowKeys([]);
          loadData();
        }}
      />
      <Modal
        title="Reject Payment"
        open={isRejectModalVisible}
        onCancel={() => {
          setIsRejectModalVisible(false);
          setCurrentPayment(null);
          setRejectReason('');
        }}
        onOk={handleConfirmReject}
        okText="Reject"
        okButtonProps={{ danger: true, loading: rejecting }}
        cancelButtonProps={{ disabled: rejecting }}
      >
        <p>Enter a reason for rejecting this payment. It will be saved against the Payment ID.</p>
        <Input.TextArea
          rows={3}
          placeholder="Reason for rejection"
          value={rejectReason}
          onChange={(e) => setRejectReason(e.target.value)}
          maxLength={200}
          showCount
        />
      </Modal>
      <div className="mb-3 d-flex justify-content-between align-items-center">
        <Input
          placeholder="Search by Lead ID, UTR or Client Number..."
          prefix={<SearchOutlined />}
          style={{ width: 300 }}
          onPressEnter={(e: any) => {
            setSearchText(e.target.value);
            setPage(1);
          }}
          onChange={(e) => {
            if (!e.target.value) {
              setSearchText("");
              setPage(1);
            }
          }}
        />
       <div className="d-flex gap-2">
        <ZohoReconcile canManage={canManage} />
        <Dropdown
          menu={{
            items: [
              { key: 'current', label: 'Download Current View', onClick: handleDownloadCurrentView },
              { key: 'all', label: 'Download All', onClick: handleDownloadAll },
            ],
          }}
          placement="bottomRight"
        >
          <Button icon={<DownloadOutlined />}>Download CSV</Button>
        </Dropdown>
        <Button
          onClick={() => {
            if (!canManage) {
              message.error("You don't have permission to authorize payments");
              return;
            }
            setIsSheetModalVisible(true);
          }}
        >
          Authorize from Sheet
        </Button>
        <Button
                type="primary" 
                onClick={handleBulkAuthorize}
                disabled={selectedRowKeys.length === 0}
                loading={loading}
              >
                Bulk Authorize ({selectedRowKeys.length})
              </Button>
       </div>
      </div>

      <Tabs
        activeKey={activeTab}
        onChange={(key) => {
          setActiveTab(key);
          setPage(1);
          setFilters({});
          setOrdering('');
          setSelectedRowKeys([]); // Clear selection when switching tabs
        }}
        className="payment-tabs"
      >
        <TabPane tab="Payments" key="payments">
          <Table
            columns={paymentColumns}
            dataSource={payments}
            rowKey="id"
            loading={loading}
            pagination={false}
            scroll={{ x: 'max-content' }}
            rowSelection={{
              selectedRowKeys,
              onChange: (newSelectedRowKeys: React.Key[]) => {
                setSelectedRowKeys(newSelectedRowKeys as number[]);
              },
              getCheckboxProps: (record: any) => ({
                disabled: record.status !== 'authenticated', // Only allow selection of authenticated payments
              }),
            }}
            onChange={(_p, tableFilters, sorter: any) => {
              setFilters(tableFilters);
              const order = sorter.order === 'ascend' ? sorter.field : sorter.order === 'descend' ? `-${sorter.field}` : '';
              setOrdering(order);
              setPage(1);
            }}
          />
        </TabPane>
        <TabPane tab="Credit" key="credit">
          <Segmented
            className="mb-3"
            value={creditView}
            onChange={(value) => {
              setCreditView(value as 'order' | 'client');
              setPage(1);
              setFilters({});
              setOrdering('');
              setSelectedRowKeys([]);
            }}
            options={[
              { label: 'By Order', value: 'order' },
              { label: 'By Client', value: 'client' },
            ]}
          />
          {creditView === 'client' ? (
            <CreditByClient
              canManage={canManage}
              searchText={searchText}
              refreshToken={refreshToken}
              onConvertCredit={handleConvertToPayment}
              onRejectCredit={handleRejectClick}
            />
          ) : (
          <Table
            columns={creditColumns}
            dataSource={credits}
            rowKey="id"
            loading={loading}
            pagination={false}
            scroll={{ x: 'max-content' }}
            rowSelection={{
              selectedRowKeys,
              onChange: (newSelectedRowKeys: React.Key[]) => {
                setSelectedRowKeys(newSelectedRowKeys as number[]);
              },
              getCheckboxProps: (record: any) => ({
                disabled: record.status !== 'authenticated', // Only allow selection of authenticated payments
              }),
            }}
            onChange={(_p, tableFilters, sorter: any) => {
              setFilters(tableFilters);
              const order = sorter.order === 'ascend' ? sorter.field : sorter.order === 'descend' ? `-${sorter.field}` : '';
              setOrdering(order);
              setPage(1);
            }}
          />
          )}
        </TabPane>
      </Tabs>

      {!isClientCreditView && (
        <Pagination
          current={page}
          total={totalItems}
          pageSize={10}
          showSizeChanger={false}
          className="mt-3 text-end"
          onChange={(p) => setPage(p)}
        />
      )}
    </div>
  );
};

export default PaymentTabs;
